// Lightweight i18n - context + hook + localStorage persistence.
// No external dependency; the two dictionaries are bundled as TS so we
// get full IDE autocomplete and type-checking on every `t.x.y` access.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import en from "./translations/en";
import tr from "./translations/tr";

export type Lang = "en" | "tr";
export type Translations = typeof en;

const DICTIONARIES: Record<Lang, Translations> = { en, tr };

const STORAGE_KEY = "lang";

function detectLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "tr") return stored;
  } catch {
    /* ignore */
  }
  const nav = (navigator.language || "").toLowerCase();
  if (nav.startsWith("tr")) return "tr";
  return "en";
}

interface I18nContextValue {
  lang: Lang;
  t: Translations;
  setLang: (l: Lang) => void;
  /** Replace {placeholder} tokens with the values in `vars`. */
  fmt: (template: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = lang;
  }, [lang]);

  const fmt = useCallback(
    (template: string, vars: Record<string, string | number> = {}): string =>
      template.replace(/\{(\w+)\}/g, (_, k) =>
        vars[k] !== undefined ? String(vars[k]) : `{${k}}`,
      ),
    [],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      lang,
      t: DICTIONARIES[lang],
      setLang: setLangState,
      fmt,
    }),
    [lang, fmt],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useT must be used inside a <LanguageProvider>");
  }
  return ctx;
}
